import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Toolbar from './Toolbar';
import { useFormContext } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import { useEffect } from 'react';
import { RichTextEditorContainer } from './styles';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Placeholder from '@tiptap/extension-placeholder';
import { theme } from '@jadwalh/ui';

export const RichTextEditor = ({
  name,
  placeholder,
  label,
  onChange,
  registerOptions,
  content,
  extensions = [],
  helperText,
}: {
  name: string;
  onChange: any;
  registerOptions: any;
  placeholder: string;
  label?: string;
  content?: string;
  extensions?: any[];
  helperText?: string;
}) => {
  const { register, formState, watch } = useFormContext();
  const { errors } = formState;

  // onChange Handler
  const onChangeHandler = (event: any) => {
    const registration = register(name, registerOptions);
    registration.onChange(event);
    if (onChange) onChange(event.target.value);
  };

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Link.configure(),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
        alignments: ['left', 'center', 'right', 'justify'],
        defaultAlignment: 'right',
      }),
      Placeholder.configure({
        placeholder,
        showOnlyWhenEditable: true,
        emptyEditorClass: 'is-editor-empty',
        emptyNodeClass: 'is-empty',
      }),
      ...extensions,
    ],
    parseOptions: {
      preserveWhitespace: 'full',
    },
    editorProps: {
      attributes: {
        dir: 'rtl',
        class: 'rtl-content',
      },
    },
    content: content || watch(name),
    onUpdate: ({ editor }) => {
      onChangeHandler({ target: { value: editor.getHTML() } });
    },
  });

  // Update the useEffect to watch for content changes
  useEffect(() => {
    if (editor && (content || watch(name))) {
      const contentToSet = content || watch(name);
      const currentContent = editor.getHTML();

      // Only update if content is significantly different
      if (
        contentToSet !== currentContent &&
        contentToSet.trim() !== currentContent.trim()
      ) {
        // Store current selection
        const selection = editor.state.selection;

        // Update content
        editor.commands.setContent(contentToSet, false, {
          preserveWhitespace: 'full',
        });

        // Restore selection only if it was valid
        if (selection && selection.from < editor.state.doc.content.size) {
          editor.commands.setTextSelection(selection);
        }
      }
    }
  }, [content, watch(name), editor]);

  return (
    <>
      {label && (
        <label style={{ marginInlineStart: 12, fontSize: 13 }}>{label}</label>
      )}
      {helperText && (
        <p
          style={{
            fontSize: '12px',
            marginInlineStart: '8px',
            color: theme.colours.gray2,
          }}
        >
          {helperText}
        </p>
      )}

      <RichTextEditorContainer 
        className="text-editor"
        onContextMenu={(e) => e.preventDefault()}
      >
        <Toolbar editor={editor} />
        <EditorContent
          editor={editor}
          {...register(name, registerOptions)}
          onChange={onChangeHandler}
        />
      </RichTextEditorContainer>
      {!!errors && !!errors[name] && (
        <div className="ErrorMessageStyle">
          <ErrorMessage
            errors={errors}
            name={name}
            render={({ message }) => <p>{message}</p>}
          />
        </div>
      )}
    </>
  );
};
